import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ReportingSchemaService } from "@gen/tracker/v1/reporting_connect";
import { ReportFormula, StatsRow } from "@gen/tracker/v1/reporting_pb";

import { createClient } from "@/lib/connect";
import { reportingSchemaKeys } from "./reporting-schema-service";

const client = createClient(ReportingSchemaService);

/**
 * «Своя метрика» (доска ReportFormula): a workspace formula is one more metric
 * of the schema, so every write reloads the schema — the new column, its
 * label and digits arrive from there, never from the dialog's own state.
 */
function useInvalidateSchema() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: reportingSchemaKeys.all });
}

export function useSaveReportFormula() {
  const invalidate = useInvalidateSchema();
  return useMutation({
    mutationFn: async (f: ReportFormula) => {
      const res = f.id ? await client.updateReportFormula({ formula: f }) : await client.createReportFormula({ formula: f });
      return res.formula;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteReportFormula() {
  const invalidate = useInvalidateSchema();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.deleteReportFormula({ id });
      return id;
    },
    onSuccess: invalidate,
  });
}

/**
 * «Проверка на текущем отчёте»: the expression parsed by the workspace's
 * registry and evaluated over the open report's totals, nothing saved. The
 * value is absent when the registry has none («—»).
 */
export function useCheckReportFormula(expression: string, totals: StatsRow | undefined) {
  return useQuery({
    queryKey: ["reporting-schema", "check-formula", expression, totals?.values] as const,
    enabled: expression.trim() !== "",
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await client.checkReportFormula({ expression, values: totals ?? new StatsRow() });
      return { expression: res.expression, operandKeys: res.operandKeys, value: res.value };
    },
  });
}

